import React, { useState, useEffect } from "react";
import { Table, Button, Spin, Typography, Tag, Input, Space } from "antd";
import { SearchOutlined, ReloadOutlined, FileExcelOutlined } from "@ant-design/icons";
import axios from "axios";
import BASEURL from "../API/Config";
import * as XLSX from "xlsx";
import moment from "moment";

const SalesReport = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASEURL}/reports/detailed-sales`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (error) {
      console.error("Error fetching detailed sales:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const exportData = data.map((order, index) => ({
      "S.No": index + 1,
      "Order ID": order.orderId,
      "Tracking Number": order.trackingNumber,
      "Customer Name": `${order.shippingInfo?.firstName || ""} ${order.shippingInfo?.lastName || ""}`,
      "Phone": order.phoneNumber,
      "Address": `${order.shippingInfo?.address}, ${order.shippingInfo?.city}, ${order.shippingInfo?.state} - ${order.shippingInfo?.pincode}`,
      "Items": order.orderItems.map(item => `${item.product?.title} (x${item.quantity})`).join(", "),
      "Total Amount": `₹${order.totalPrice}`,
      "Payment Method": order.paymentInfo?.method,
      "Payment Status": order.paymentInfo?.paymentStatus,
      "Order Status": order.orderStatus,
      "Delivery Boy": order.deliveryboy ? `${order.deliveryboy.firstname} ${order.deliveryboy.lastname}` : "Not Assigned",
      "Created Date": moment(order.createdAt).format("YYYY-MM-DD HH:mm"),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sales Details");
    XLSX.writeFile(wb, "Full_Sales_Report.xlsx");
  };

  const filteredData = data.filter((item) =>
    item.orderId?.toLowerCase().includes(searchText.toLowerCase()) ||
    String(item.phoneNumber || "").includes(searchText) ||
    item.shippingInfo?.firstName?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
  {
    title: "S.No",
    key: "index",
    width: 50,
    align: "center",
    render: (_, __, i) => (
      <span style={{ fontSize: 12, color: "#6b7280" }}>
        {i + 1}
      </span>
    ),
  },

  {
    title: "Order ID",
    dataIndex: "orderId",
    key: "orderId",
    width: 90,
    ellipsis: true,
  },

  {
    title: "Customer",
    key: "customer",
    width: 140,
    ellipsis: true,
    render: (_, r) => (
      <span style={{ fontWeight: 600, fontSize: 12 }}>
        {`${r.shippingInfo?.firstName || ""} ${
          r.shippingInfo?.lastName || ""
        }`}
      </span>
    ),
  },

  {
    title: "Date",
    dataIndex: "createdAt",
    width: 95,
    align: "center",
    render: (date) => (
      <span style={{ fontSize: 12 }}>
        {moment(date).format("DD-MM-YY")}
      </span>
    ),
  },

  {
    title: "Amount",
    dataIndex: "totalPrice",
    width: 80,
    align: "right",
    render: (a) => (
      <span style={{ fontWeight: 600, fontSize: 12 }}>
        ₹{a}
      </span>
    ),
  },

  {
    title: "Status",
    dataIndex: "orderStatus",
    width: 110,
    align: "center",
    render: (s) => (
      <Tag
        color={
          s === "Delivered"
            ? "green"
            : s === "cancelled"
            ? "red"
            : "blue"
        }
        style={{
          borderRadius: 20,
          fontSize: 11,
          padding: "0 8px",
          margin: 0,
        }}
      >
        {s}
      </Tag>
    ),
  },

  {
    title: "Payment",
    key: "payment",
    width: 100,
    ellipsis: true,
    render: (_, r) => (
      <span style={{ fontSize: 12 }}>
        {r.paymentInfo?.method}
      </span>
    ),
  },
];

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1 }}>Reports</div>
          <h2 style={{ margin: 0, fontFamily: "Outfit,sans-serif", fontSize: 22, fontWeight: 700, color: "#212121" }}>Detailed Sales Report</h2>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchData} style={{ borderRadius: 4 }}>Refresh</Button>
        </Space>
      </div>

      <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8ecf0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "nowrap" }}>
          <span style={{ fontWeight: 700, color: "#212121", fontSize: 15, whiteSpace: "nowrap" }}>Sales Transactions</span>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, justifyContent: "flex-end" }}>
            <Input 
              prefix={<SearchOutlined style={{ color: "#9ca3af" }} />} 
              placeholder="Search ID, Customer, Phone..."
              value={searchText} 
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 220, borderRadius: 4 }} 
            />
            <Button 
              type="primary" 
              icon={<FileExcelOutlined />} 
              onClick={handleExport}
              style={{ background: "#107c41", borderColor: "#107c41", borderRadius: 4, fontWeight: 600 }}
            >
              Export Excel
            </Button>
          </div>
        </div>
        
        {loading ? (
          <div style={{ textAlign: "center", padding: "100px" }}><Spin size="large" tip="Loading data..." /></div>
        ) : (
          <Table
            dataSource={filteredData}
            columns={columns}
            rowKey="_id"
            scroll={{ x: 800 }}
            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"],
              showTotal: (total) => `Total ${total} entries`,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'] }}
            size="middle"
          />
        )}
      </div>
    </div>
  );
};

export default SalesReport;
